import React from 'react';
import { MatchPlay } from '../../types/matchplay';
import { Trophy, Users } from 'lucide-react';

interface MatchPlayLeaderboardProps {
  matches: MatchPlay[];
  onMatchSelect: (matchId: string) => void;
  selectedTournament?: Tournament | null;
}

export function MatchPlayLeaderboard({ matches, onMatchSelect, selectedTournament }: MatchPlayLeaderboardProps) {
  // Filter matches for current tournament
  const tournamentMatches = matches.filter(match => 
    match.team1.players.some(p => p.tournamentId === selectedTournament?.id) ||
    match.team2.players.some(p => p.tournamentId === selectedTournament?.id)
  );

  const getMatchResult = (match: MatchPlay) => {
    if (match.status === 'complete' && match.result) {
      return match.result === 'AS' ? 'AS Final' : `${match.result} Final`;
    }

    const team1Wins = match.holes.filter(h => h.winner === 'team1').length;
    const team2Wins = match.holes.filter(h => h.winner === 'team2').length;
    const holesPlayed = match.holes.length;
    
    if (holesPlayed === 0) return 'AS';

    const lead = team1Wins - team2Wins;
    if (lead === 0) return `AS thru ${holesPlayed}`;
    
    const absLead = Math.abs(lead);
    return `${absLead}${lead > 0 ? 'U' : 'D'} thru ${holesPlayed}`;
  };

  const formatMatchTitle = (match: MatchPlay) => {
    const team1Name = match.team1.players[0].team || 'Team A';
    const team2Name = match.team2.players[0].team || 'Team B';

    switch (match.format) {
      case 'singles':
        return `${match.team1.players[0].name} (${team1Name}) vs ${match.team2.players[0].name} (${team2Name})`;
      case 'fourball':
      case 'foursomes':
        return `${match.team1.players[0].name}/${match.team1.players[1].name} (${team1Name}) vs ${match.team2.players[0].name}/${match.team2.players[1].name} (${team2Name})`;
      default:
        return 'Unknown Match Format';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
        <Trophy className="w-6 h-6" />
        Match Play Results
      </h2>

      <div className="space-y-4">
        {tournamentMatches.map(match => (
          <button
            key={match.id}
            onClick={() => onMatchSelect(match.id)}
            className="w-full p-4 rounded-lg border-2 border-gray-200 hover:border-primary-500 transition-colors text-left"
          >
            <div className="flex justify-between items-start mb-2">
              <div>
                <div className="font-medium">{formatMatchTitle(match)}</div>
                <div className="text-sm text-gray-600 flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  {match.format.charAt(0).toUpperCase() + match.format.slice(1)}
                </div>
              </div>
              <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                match.status === 'complete'
                  ? match.result === 'AS' 
                    ? 'bg-gray-100 text-gray-800'
                    : 'bg-green-100 text-green-800'
                  : match.status === 'dormie'
                  ? 'bg-amber-100 text-amber-800'
                  : 'bg-blue-100 text-blue-800'
              }`}>
                {getMatchResult(match)}
              </div>
            </div>
            <div className="text-sm text-gray-500">
              {new Date(match.date).toLocaleDateString()}
            </div>
          </button>
        ))}

        {tournamentMatches.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No matches played yet
          </div>
        )}
      </div>
    </div>
  );
}